import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ukWhen as fmtWhen, ukStamp } from "@/lib/time";
import { api } from "@/lib/api";
import { Loading, PageHead } from "@/components/hub/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, MessageSquare, Plus, Search, Send, Building2, Handshake, ChevronLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { RemoveDialog } from "@/components/hub/RemoveDialog";
import { ChatAttachButton, ChatAttachment } from "@/components/portal/ChatAttach";
import { ConnieMark } from "@/components/connie/ConnieMark";
import { ConnieText } from "@/components/connie/ConnieText";

const KIND_CHIP = {
  lender: "bg-[#0055FF]/[0.08] text-[#0055FF] border-[#0055FF]/25",
  referrer: "bg-teal-50 text-teal-700 border-teal-200",
};
const KIND_LABEL = { lender: "Lender", referrer: "Introducer" };

export default function HubMessages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);

  const loadThreads = useCallback(async () => {
    try { const r = await api.get("/hub/chat/threads"); setThreads(r.data.items || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 8000);
    return () => clearInterval(t);
  }, [loadThreads]);

  const filtered = useMemo(() => threads.filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (t.party_name || "").toLowerCase().includes(s) || (t.party_email || "").toLowerCase().includes(s);
  }), [threads, q]);
  const active = threads.find((t) => t.id === activeId) || null;

  return (
    <div className="space-y-5" data-testid="hub-messages">
      <PageHead eyebrow="Messages" title="Partner conversations"
        sub="Live in-house messaging with your lenders and introducers. Partners chat from the bubble in their portal."
        right={
          <Button onClick={() => setOpenNew(true)} className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="hub-chat-new">
            <Plus className="size-3.5 mr-1" /> New conversation
          </Button>
        } />

      <div className="card overflow-hidden grid lg:grid-cols-[320px_1fr] h-[calc(100dvh-190px)] lg:h-[calc(100vh-230px)] min-h-[420px]">
        {/* Thread list */}
        <div className={`border-r border-slate-200 flex-col min-h-0 ${active ? "hidden lg:flex" : "flex"}`}>
          <div className="p-3 border-b border-slate-100 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partners…" className="pl-9 h-9 text-[13px]" data-testid="hub-chat-search" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <Loading />
            ) : filtered.length === 0 ? (
              <div className="text-center py-14 px-6 text-[12.5px] text-slate-500">
                <MessageSquare className="size-6 text-slate-300 mx-auto mb-2" />
                No conversations yet — partners can message you from their portal, or start one yourself.
              </div>
            ) : (
              filtered.map((t) => (
                <button key={t.id} onClick={() => setActiveId(t.id)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${activeId === t.id ? "bg-[#0055FF]/[0.05]" : "hover:bg-slate-50"}`}
                        data-testid={`hub-thread-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="text-[13px] font-semibold text-slate-900 sm:truncate flex-1">{t.party_name}</div>
                    {t.admin_unread > 0 && (
                      <span className="grid place-items-center min-w-5 h-5 px-1.5 rounded-full bg-[#0055FF] text-white text-[10px] font-bold shrink-0" data-testid={`hub-thread-unread-${t.id}`}>{t.admin_unread}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5">
                    <span className={`inline-flex items-center rounded-full border px-1.5 h-4 text-[9px] font-semibold uppercase tracking-wider ${KIND_CHIP[t.kind]}`}>{KIND_LABEL[t.kind]}</span>
                    <span className="text-[10.5px] text-slate-400">{fmtWhen(t.last_message_at)}</span>
                  </div>
                  {t.last_body && (
                    <div className="mt-1 text-[12px] text-slate-500 truncate">
                      {t.last_sender === "admin" ? <span className="text-slate-400">You: </span> : null}{t.last_body}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Conversation */}
        {active ? (
          <Conversation key={active.id} thread={active} onRead={loadThreads} onBack={() => setActiveId(null)} />
        ) : (
          <div className="hidden lg:grid place-items-center text-center px-8">
            <div>
              <div className="mx-auto grid place-items-center size-12 rounded-2xl bg-slate-100 text-slate-400"><MessageSquare className="size-5" /></div>
              <div className="mt-3 text-[14px] font-semibold text-slate-700">Pick a conversation</div>
              <div className="mt-1 text-[12.5px] text-slate-500 max-w-xs">Select a thread on the left, or start a new conversation with any lender or introducer.</div>
            </div>
          </div>
        )}
      </div>

      <NewConversationDialog open={openNew} onOpenChange={setOpenNew}
        onOpened={(t) => { setOpenNew(false); loadThreads(); setActiveId(t.id); }} />
    </div>
  );
}

function Conversation({ thread, onRead, onBack }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [connie, setConnie] = useState(false);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/hub/chat/threads/${thread.id}/messages`);
      setMessages(r.data.items || []);
      onRead?.();
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [thread.id]); // eslint-disable-line

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  const stick = useRef(true);
  useEffect(() => {
    const el = scrollRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    stick.current = true;
    setSending(true);
    try {
      await api.post(`/hub/chat/threads/${thread.id}/messages`, { body });
      setInput("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const askConnie = async () => {
    if (connie) return;
    stick.current = true;
    setConnie(true);
    try {
      await api.post(`/hub/connie/threads/${thread.id}/reply`);
      toast.success("Connie replied");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Connie couldn't reply just now"); }
    finally { setConnie(false); }
  };

  return (
    <div className="flex flex-col min-h-0" data-testid="hub-conversation">
      <div className="px-4 lg:px-5 py-3.5 border-b border-slate-100 flex items-center gap-2.5 shrink-0">
        {onBack && (
          <button onClick={onBack} className="lg:hidden grid place-items-center size-9 -ml-1 rounded-xl text-slate-500 active:bg-slate-100"
                  aria-label="Back to conversations" data-testid="hub-conversation-back">
            <ChevronLeft className="size-5" />
          </button>
        )}
        <span className={`grid place-items-center size-9 rounded-xl border ${KIND_CHIP[thread.kind]}`}>
          {thread.kind === "lender" ? <Building2 className="size-4" /> : <Handshake className="size-4" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-slate-900 sm:truncate">{thread.party_name}</div>
          <div className="text-[11.5px] text-slate-500 truncate">{KIND_LABEL[thread.kind]}{thread.party_contact && thread.party_contact !== thread.party_name ? ` · ${thread.party_contact}` : ""} · {thread.party_email}</div>
        </div>
        <button onClick={() => setRemoving(true)} title="Delete this conversation"
                className="grid size-9 shrink-0 place-items-center rounded-xl text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                data-testid="hub-conversation-delete">
          <Trash2 className="size-4" />
        </button>
        <RemoveDialog kind="message" id={thread.id} name={`the conversation with ${thread.party_name}`}
                      open={removing} onOpenChange={setRemoving}
                      onDeleted={() => { onBack?.(); onRead?.(); }} />
      </div>

      <div ref={scrollRef}
           onScroll={(e) => { const el = e.currentTarget; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; }}
           className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50" data-testid="hub-conversation-messages">
        {loading ? (
          <div className="grid place-items-center h-full text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <div className="grid place-items-center h-full text-[12.5px] text-slate-500">No messages yet — say hello.</div>
        ) : (
          messages.map((m) => {
            if (m.sender === "system") {
              return (
                <div key={m.id} className="flex justify-center" data-testid={`hub-chat-receipt-${m.id}`}>
                  <div className="max-w-[80%] rounded-xl border border-teal-200 bg-teal-50/70 px-3 py-2 text-center text-[11.5px] leading-relaxed text-teal-800">
                    <span className="font-semibold">Auto-receipt sent</span> · {m.body} · {ukStamp(m.created_at)}
                  </div>
                </div>
              );
            }
            if (m.sender === "connie") return <ConnieBubble key={m.id} m={m} />;
            const mine = m.sender === "admin";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[70%]">
                  <div className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap break-words ${
                    mine ? "bg-[#0B1220] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md shadow-sm"
                  }`}>
                    {m.body}
                    <ChatAttachment att={m.attachment} mine={mine} />
                  </div>
                  <div className={`mt-1 text-[10px] text-slate-400 ${mine ? "text-right" : ""}`}>
                    {mine ? "You" : m.sender_name} · {ukStamp(m.created_at)}
                    {VIA[m.via] ? <span className="ml-1 text-slate-400" data-testid={`msg-via-${m.via}-${m.id}`}>· {VIA[m.via]}</span> : null}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="relative border-t border-slate-200 bg-white p-3 flex items-end gap-2 shrink-0">
        <ChatAttachButton casesPath={`/hub/chat/threads/${thread.id}/cases`}
                          uploadPath={`/hub/chat/threads/${thread.id}/attachments`}
                          onSent={load} testPrefix="hub-chat-attach" width={440} />
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={`Message ${thread.party_name}…`}
          className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0055FF]/50 focus:bg-white transition-colors max-h-28"
          data-testid="hub-chat-composer"
        />
        <button onClick={askConnie} disabled={connie} title="Connie reads the conversation and replies for you"
                className="connie-glow-btn inline-flex items-center gap-2 h-10 pl-1.5 pr-3.5 rounded-xl border border-slate-200 bg-white text-[12.5px] font-semibold text-slate-800 disabled:opacity-60 shrink-0"
                data-testid="hub-chat-connie-reply">
          <ConnieMark size={26} state={connie ? "thinking" : "idle"} orbit={false} testId="hub-chat-connie-mark" />
          {connie ? "Writing…" : "Reply with Connie"}
        </button>
        <button onClick={send} disabled={sending || !input.trim()}
                className="grid place-items-center size-10 rounded-xl bg-[#0055FF] text-white hover:bg-[#0044CC] disabled:opacity-40 transition-colors shrink-0"
                data-testid="hub-chat-send">
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </button>
      </div>
    </div>
  );
}

const VIA = { email: "via email", sms: "by text" };

function ConnieBubble({ m }) {
  return (
    <div className="flex justify-end" data-testid={`hub-chat-connie-${m.id}`}>
      <div className="max-w-[72%] flex items-start gap-2.5 flex-row-reverse">
        <ConnieMark size={28} orbit={false} testId={`hub-chat-connie-mark-${m.id}`} />
        <div>
          <div className="connie-bubble break-words text-[13px]" style={{ borderRadius: "16px 5px 16px 16px" }}>
            <ConnieText text={m.body} />
          </div>
          <div className="mt-1 text-[10px] text-slate-400 text-right">
            Connie · {m.auto ? "answered automatically" : "replied for you"}{VIA[m.via] ? ` ${VIA[m.via]}` : ""} · {ukStamp(m.created_at)}
            {m.handed && <span className="ml-1.5 font-semibold text-amber-700" data-testid={`hub-chat-connie-handed-${m.id}`}>· needs you</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function NewConversationDialog({ open, onOpenChange, onOpened }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true); setQ("");
    api.get("/hub/chat/contacts").then((r) => setContacts(r.data.items || [])).catch(() => setContacts([])).finally(() => setLoading(false));
  }, [open]);

  const filtered = contacts.filter((c) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s);
  });

  const start = async (c) => {
    setBusy(c.party_id);
    try {
      const r = await api.post("/hub/chat/threads", { kind: c.kind, party_id: c.party_id });
      onOpened?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open conversation"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="hub-chat-contacts-dialog">
        <DialogHeader>
          <DialogTitle>Start a conversation</DialogTitle>
          <DialogDescription>Message any lender or introducer — they'll get it in their portal chat and by email.</DialogDescription>
        </DialogHeader>
        <div className="relative shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" data-testid="hub-chat-contacts-search" />
        </div>
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 min-h-[200px]">
          {loading ? (
            <Loading />
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-[12.5px] text-slate-500">No partners found.</div>
          ) : (
            filtered.map((c) => (
              <button key={`${c.kind}-${c.party_id}`} onClick={() => start(c)} disabled={busy === c.party_id}
                      className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 text-left hover:border-[#0055FF]/40 transition-colors"
                      data-testid={`hub-chat-contact-${c.party_id}`}>
                <span className={`grid place-items-center size-8 rounded-lg border shrink-0 ${KIND_CHIP[c.kind]}`}>
                  {c.kind === "lender" ? <Building2 className="size-3.5" /> : <Handshake className="size-3.5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{c.email}{!c.activated ? " · not yet activated" : ""}</div>
                </div>
                {busy === c.party_id && <Loader2 className="size-4 animate-spin text-slate-400" />}
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

